/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import type { ComponentType, DragEvent, RefObject } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ClipboardList,
  Eye,
  Flag,
  GripVertical,
  Loader2,
  ListChecks,
  MessageSquarePlus,
  Mic,
  Navigation,
  PencilLine,
  PenTool,
  Play,
  Plus,
  Save,
  Sparkles,
  SlidersHorizontal,
  Square,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Type,
  Users,
  X,
} from "lucide-react";

import { buildExperimentPreviewUrl } from "../../config/previewUrl";
import {
  getQuestionsBySection,
  isTaggedFeedbackQuestion,
  LIKERT_DEFAULT_OPTIONS,
} from "../../utils/feedbackFlow";
import {
  navigatePreviewToRoute,
  scrollPreviewToElement,
} from "../../utils/previewMessaging";

import type { CoreResearchQuestionOption, TaggingMode } from "./tagging.types";
import type {
  FeedbackFlow,
  FeedbackFlowContent,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionInputType,
  FeedbackFlowQuestionSection,
  FeedbackFlowQuestionSentiment,
} from "../../types/experiment.types";

type SetupTabId = "intro" | "demographics" | "personalInfo" | "outro";
type SidebarWorkspace = "questions" | "setup";
type PreviewMode = "intro" | "product" | "outro";
type DragPosition = "before" | "after";
type ChoiceInputType = Extract<
  FeedbackFlowQuestionInputType,
  "multiple_choice" | "checkbox" | "likert_scale"
>;
type OptionDrafts = Record<ChoiceInputType, string[]>;
type QuestionReactionDraft = {
  sentiment: FeedbackFlowQuestionSentiment | null;
  dislikeReason: string;
};

const QUESTION_TYPE_OPTIONS: Array<{
  value: FeedbackFlowQuestionInputType;
  label: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  { value: "text", label: "Free text", icon: Type },
  { value: "multiple_choice", label: "Multiple choice", icon: ListChecks },
  { value: "checkbox", label: "Checkbox", icon: Square },
  { value: "likert_scale", label: "Likert scale", icon: SlidersHorizontal },
  { value: "speech_to_text", label: "Speech to text", icon: Mic },
];

const QUESTION_TYPE_LABEL: Record<FeedbackFlowQuestionInputType, string> = {
  text: "Free text",
  multiple_choice: "Multiple choice",
  checkbox: "Checkbox",
  likert_scale: "Likert scale",
  speech_to_text: "Speech to text",
};

const SECTION_LABEL: Record<FeedbackFlowQuestionSection, string> = {
  demographics: "Demographics",
  personal_info: "Personal Info",
  prototype: "Interview Questions",
};

const RESEARCH_QUESTION_PILL_STYLES = [
  "border-[#315899]/50 bg-[#18284A] text-[#5B87FF]",
  "border-[#2A6F54]/50 bg-[#12362A] text-[#5BD49B]",
  "border-[#7A541E]/50 bg-[#3A2813] text-[#F2B13C]",
];

const needsOptions = (type: FeedbackFlowQuestionInputType): boolean =>
  type === "multiple_choice" || type === "checkbox" || type === "likert_scale";

const isChoiceInputType = (
  type: FeedbackFlowQuestionInputType,
): type is ChoiceInputType => needsOptions(type);

const defaultOptionsForType = (
  type: FeedbackFlowQuestionInputType,
): string[] => {
  if (type === "likert_scale") {
    return [...LIKERT_DEFAULT_OPTIONS];
  }
  if (type === "multiple_choice" || type === "checkbox") {
    return ["", ""];
  }
  return [];
};

const createOptionDrafts = (
  inputType: FeedbackFlowQuestionInputType = "text",
  options: string[] = [],
): OptionDrafts => ({
  multiple_choice:
    inputType === "multiple_choice" && options.length > 0
      ? [...options]
      : defaultOptionsForType("multiple_choice"),
  checkbox:
    inputType === "checkbox" && options.length > 0
      ? [...options]
      : defaultOptionsForType("checkbox"),
  likert_scale:
    inputType === "likert_scale" && options.length > 0
      ? [...options]
      : defaultOptionsForType("likert_scale"),
});

const getQuestionRoute = (question: FeedbackFlowQuestion): string | undefined =>
  question.taggedTarget?.route;

const LoadingBlock = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded-md bg-white/10 ${className}`} />
);

const reorderQuestionIds = (
  questionIds: string[],
  draggedQuestionId: string,
  targetQuestionId: string,
  position: DragPosition,
): string[] => {
  if (
    draggedQuestionId === targetQuestionId &&
    (position === "before" || position === "after")
  ) {
    return questionIds;
  }

  const remainingIds = questionIds.filter((questionId) => questionId !== draggedQuestionId);
  const targetIndex = remainingIds.indexOf(targetQuestionId);

  if (targetIndex < 0) {
    return questionIds;
  }

  remainingIds.splice(targetIndex + (position === "after" ? 1 : 0), 0, draggedQuestionId);
  return remainingIds;
};

const mergeVisibleQuestionIdsIntoSectionOrder = (
  sectionQuestionIds: string[],
  reorderedVisibleQuestionIds: string[],
): string[] => {
  const visibleQuestionIdSet = new Set(reorderedVisibleQuestionIds);
  let visibleQuestionIndex = 0;

  return sectionQuestionIds.map((questionId) => {
    if (!visibleQuestionIdSet.has(questionId)) {
      return questionId;
    }

    const nextVisibleQuestionId =
      reorderedVisibleQuestionIds[visibleQuestionIndex] ?? questionId;
    visibleQuestionIndex += 1;
    return nextVisibleQuestionId;
  });
};

const getQuestionReactionDraft = (
  question: FeedbackFlowQuestion,
  draft?: QuestionReactionDraft,
): QuestionReactionDraft => ({
  sentiment: draft?.sentiment ?? question.sentiment ?? null,
  dislikeReason: draft?.dislikeReason ?? question.dislikeReason ?? "",
});

interface TaggingQuestionsPanelProps {
  feedbackFlow: FeedbackFlow;
  coreResearchQuestions: CoreResearchQuestionOption[];
  updateIntro: (
    updater: (current: FeedbackFlowContent) => FeedbackFlowContent,
  ) => void;
  updateOutro: (
    updater: (current: FeedbackFlowContent) => FeedbackFlowContent,
  ) => void;
  saveQuestion: (question: FeedbackFlowQuestion) => Promise<boolean>;
  deleteQuestion: (questionId: string) => Promise<boolean>;
  reorderQuestions: (
    section: FeedbackFlowQuestionSection,
    questionIds: string[],
  ) => Promise<boolean>;
  addQuestion: (section: FeedbackFlowQuestionSection) => void;
  setSectionEnabled: (
    section: Extract<FeedbackFlowQuestionSection, "demographics" | "personal_info">,
    enabled: boolean,
  ) => void;
  onTagExistingQuestion: (questionId: string) => void;
  previewFrameRef?: RefObject<HTMLIFrameElement | null>;
  previewBaseUrl?: string;
  iframeRoute: string;
  taggingMode: TaggingMode;
  onTaggingModeChange: (mode: TaggingMode) => void;
  activePreview: PreviewMode;
  setActivePreview: (view: PreviewMode) => void;
  isInitializing: boolean;
  isSyncing: boolean;
  isGenerating?: boolean;
  generationError?: string | null;
  dismissGenerationError?: () => void;
  isPreviewReady?: boolean;
  isReadOnly?: boolean;
}

export function TaggingQuestionsPanel({
  feedbackFlow,
  coreResearchQuestions,
  updateIntro,
  updateOutro,
  saveQuestion,
  deleteQuestion,
  reorderQuestions,
  addQuestion,
  setSectionEnabled,
  onTagExistingQuestion,
  previewFrameRef,
  previewBaseUrl,
  iframeRoute,
  taggingMode,
  onTaggingModeChange,
  activePreview,
  setActivePreview,
  isInitializing,
  isSyncing,
  isGenerating = false,
  isPreviewReady = true,
  isReadOnly = false,
}: TaggingQuestionsPanelProps) {
  const [sidebarWorkspace, setSidebarWorkspace] =
    useState<SidebarWorkspace>("questions");
  const [setupTab, setSetupTab] = useState<SetupTabId>("intro");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftQuestion, setDraftQuestion] = useState("");
  const [draftInputType, setDraftInputType] =
    useState<FeedbackFlowQuestionInputType>("text");
  const [draftPremiseId, setDraftPremiseId] = useState("");
  const [draftOptions, setDraftOptions] = useState<OptionDrafts>(() =>
    createOptionDrafts(),
  );
  const [reactionDrafts, setReactionDrafts] = useState<
    Record<string, QuestionReactionDraft>
  >({});
  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(null);
  const [reorderingSection, setReorderingSection] =
    useState<FeedbackFlowQuestionSection | null>(null);
  const [draggingQuestionId, setDraggingQuestionId] = useState<string | null>(null);
  const [draggingSection, setDraggingSection] =
    useState<FeedbackFlowQuestionSection | null>(null);
  const [dragOverQuestionId, setDragOverQuestionId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<DragPosition | null>(
    null,
  );
  const [reorderWarning, setReorderWarning] = useState<string | null>(null);
  const [selectedResearchQuestionId, setSelectedResearchQuestionId] =
    useState<string | null>(null);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const questionInputRef = useRef<HTMLTextAreaElement | null>(null);
  const isEditingDisabled = isInitializing || isReadOnly;

  const demographicsQuestions = useMemo(
    () => getQuestionsBySection(feedbackFlow, "demographics"),
    [feedbackFlow],
  );
  const personalInfoQuestions = useMemo(
    () => getQuestionsBySection(feedbackFlow, "personal_info"),
    [feedbackFlow],
  );
  const prototypeQuestions = useMemo(
    () => getQuestionsBySection(feedbackFlow, "prototype"),
    [feedbackFlow],
  );
  const allQuestions = feedbackFlow.questions;
  const coreResearchQuestionIds = useMemo(
    () => new Set(coreResearchQuestions.map((premise) => premise.id)),
    [coreResearchQuestions],
  );
  const coreResearchQuestionById = useMemo(
    () =>
      new Map(
        coreResearchQuestions.map((premise, index) => [
          premise.id,
          {
            ...premise,
            index,
          },
        ]),
      ),
    [coreResearchQuestions],
  );

  const resolveDraftPremiseId = useCallback(
    (question: FeedbackFlowQuestion): string => {
      const premiseId = question.premiseId?.trim();
      if (premiseId) {
        return premiseId;
      }

      if (question.section === "prototype" && coreResearchQuestions.length === 1) {
        return coreResearchQuestions[0].id;
      }

      return "";
    },
    [coreResearchQuestions],
  );

  useEffect(() => {
    let nextEditingId: string | null = null;
    for (const question of allQuestions) {
      if (!knownIdsRef.current.has(question.id)) {
        knownIdsRef.current.add(question.id);
        if (!question.question.trim()) {
          nextEditingId = question.id;
        }
      }
    }
    if (nextEditingId && !isReadOnly) {
      const nextQuestion = allQuestions.find(
        (question) => question.id === nextEditingId,
      );
      if (nextQuestion) {
        setEditingId(nextQuestion.id);
        setDraftQuestion(nextQuestion.question);
        setDraftInputType(nextQuestion.inputType);
        setDraftPremiseId(resolveDraftPremiseId(nextQuestion));
        setDraftOptions(
          createOptionDrafts(nextQuestion.inputType, nextQuestion.options),
        );
      }
    }
  }, [allQuestions, isReadOnly, resolveDraftPremiseId]);

  useEffect(() => {
    if (!isReadOnly) {
      return;
    }

    setEditingId(null);
    setDraggingQuestionId(null);
    setDraggingSection(null);
    setDragOverQuestionId(null);
    setDragOverPosition(null);
  }, [isReadOnly]);

  useEffect(() => {
    if (editingId) {
      questionInputRef.current?.focus();
    }
  }, [editingId]);

  useEffect(() => {
    if (!editingId || draftPremiseId || coreResearchQuestions.length !== 1) {
      return;
    }

    const editingQuestion = allQuestions.find(
      (question) => question.id === editingId,
    );
    if (editingQuestion?.section === "prototype") {
      setDraftPremiseId(coreResearchQuestions[0].id);
    }
  }, [allQuestions, coreResearchQuestions, draftPremiseId, editingId]);

  useEffect(() => {
    if (activePreview !== "product") {
      setSidebarWorkspace("setup");
    }
  }, [activePreview]);

  useEffect(() => {
    if (!reorderWarning) {
      return;
    }

    const timer = window.setTimeout(() => {
      setReorderWarning(null);
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [reorderWarning]);

  useEffect(() => {
    if (
      selectedResearchQuestionId &&
      !coreResearchQuestionIds.has(selectedResearchQuestionId)
    ) {
      setSelectedResearchQuestionId(null);
    }
  }, [coreResearchQuestionIds, selectedResearchQuestionId]);

  const researchQuestionCounts = useMemo(() => {
    const counts = new Map<string, number>(
      coreResearchQuestions.map((question) => [question.id, 0]),
    );

    for (const question of prototypeQuestions) {
      const premiseId = question.premiseId?.trim();
      if (premiseId && counts.has(premiseId)) {
        counts.set(premiseId, (counts.get(premiseId) ?? 0) + 1);
      }
    }

    return counts;
  }, [coreResearchQuestions, prototypeQuestions]);

  const filteredPrototypeQuestions = useMemo(() => {
    if (!selectedResearchQuestionId) {
      return prototypeQuestions;
    }
    return prototypeQuestions.filter(
      (question) => question.premiseId?.trim() === selectedResearchQuestionId,
    );
  }, [prototypeQuestions, selectedResearchQuestionId]);

  const setupTabs: Array<{
    id: SetupTabId;
    label: string;
    shortLabel: string;
    icon: ComponentType<{ className?: string }>;
    enabled: boolean;
    summary: string;
    status: string;
  }> = [
    {
      id: "intro",
      label: "Intro",
      shortLabel: "Intro",
      icon: Play,
      enabled: feedbackFlow.intro.enabled,
      summary: "Welcome screen copy and optional terms",
      status: feedbackFlow.intro.enabled
        ? feedbackFlow.intro.description.trim() || feedbackFlow.intro.terms?.trim()
          ? "Content ready"
          : "Needs copy"
        : "Disabled",
    },
    {
      id: "demographics",
      label: "Demographics",
      shortLabel: "Demo",
      icon: Users,
      enabled: feedbackFlow.demographicsEnabled,
      summary: "Optional participant background questions",
      status: `${demographicsQuestions.length} question${
        demographicsQuestions.length === 1 ? "" : "s"
      }`,
    },
    {
      id: "personalInfo",
      label: "Personal Info",
      shortLabel: "Info",
      icon: ClipboardList,
      enabled: feedbackFlow.personalInfoEnabled,
      summary: "Contact and profile details from participants",
      status: `${personalInfoQuestions.length} question${
        personalInfoQuestions.length === 1 ? "" : "s"
      }`,
    },
    {
      id: "outro",
      label: "Outro",
      shortLabel: "Outro",
      icon: Flag,
      enabled: feedbackFlow.outro.enabled,
      summary: "Thank-you screen and follow-up copy",
      status: feedbackFlow.outro.enabled
        ? feedbackFlow.outro.description.trim() || feedbackFlow.outro.terms?.trim()
          ? "Content ready"
          : "Needs copy"
        : "Disabled",
    },
  ];
  const activeSetupTab =
    setupTabs.find((tab) => tab.id === setupTab) ?? setupTabs[0];

  const openSetupWorkspace = useCallback((tabId: SetupTabId) => {
    setSetupTab(tabId);
    setSidebarWorkspace("setup");
  }, []);

  const openQuestionsWorkspace = useCallback(() => {
    setSidebarWorkspace("questions");
    setActivePreview("product");
  }, [setActivePreview]);

  const handleSetupShortcut = useCallback(
    (tabId: SetupTabId) => {
      openSetupWorkspace(tabId);
      if (tabId === "intro" && feedbackFlow.intro.enabled) {
        setActivePreview("intro");
      }
      if (tabId === "outro" && feedbackFlow.outro.enabled) {
        setActivePreview("outro");
      }
    },
    [
      feedbackFlow.intro.enabled,
      feedbackFlow.outro.enabled,
      openSetupWorkspace,
      setActivePreview,
    ],
  );

  const openSetupOverview = useCallback(() => {
    setSidebarWorkspace("setup");
  }, []);

  const resetEditor = useCallback(() => {
    setEditingId(null);
    setDraftQuestion("");
    setDraftInputType("text");
    setDraftPremiseId("");
    setDraftOptions(createOptionDrafts());
  }, []);

  const startEditing = useCallback(
    (question: FeedbackFlowQuestion) => {
      setEditingId(question.id);
      setDraftQuestion(question.question);
      setDraftInputType(question.inputType);
      setDraftPremiseId(resolveDraftPremiseId(question));
      setDraftOptions(createOptionDrafts(question.inputType, question.options));
    },
    [resolveDraftPremiseId],
  );

  const clearReactionDraft = useCallback((questionId: string) => {
    setReactionDrafts((previous) => {
      if (!previous[questionId]) {
        return previous;
      }

      const nextDrafts = { ...previous };
      delete nextDrafts[questionId];
      return nextDrafts;
    });
  }, []);

  const setQuestionReactionDraft = useCallback(
    (
      question: FeedbackFlowQuestion,
      updates: Partial<QuestionReactionDraft>,
    ) => {
      setReactionDrafts((previous) => {
        const currentDraft = getQuestionReactionDraft(
          question,
          previous[question.id],
        );

        return {
          ...previous,
          [question.id]: {
            ...currentDraft,
            ...updates,
          },
        };
      });
    },
    [],
  );

  const saveQuestionReaction = useCallback(
    async (
      question: FeedbackFlowQuestion,
      sentiment: FeedbackFlowQuestionSentiment,
      dislikeReason?: string,
    ): Promise<boolean> => {
      if (question.source !== "ai_generated") {
        return false;
      }

      const trimmedDislikeReason = dislikeReason?.trim() ?? "";
      if (sentiment === "dislike" && !trimmedDislikeReason) {
        return false;
      }

      setSavingQuestionId(question.id);
      const didSave = await saveQuestion({
        ...question,
        sentiment,
        dislikeReason:
          sentiment === "dislike" ? trimmedDislikeReason : null,
      });
      setSavingQuestionId((current) =>
        current === question.id ? null : current,
      );

      if (didSave || sentiment === "like") {
        clearReactionDraft(question.id);
      }

      return didSave;
    },
    [clearReactionDraft, saveQuestion],
  );

  const handleSave = useCallback(
    async (questionId: string) => {
      const question = allQuestions.find((item) => item.id === questionId);
      if (!question) {
        return;
      }

      const nextQuestionText = draftQuestion.trim();
      if (!nextQuestionText) {
        return;
      }

      const nextPremiseId =
        question.section === "prototype" ? draftPremiseId.trim() : null;
      if (
        question.section === "prototype" &&
        (!nextPremiseId || !coreResearchQuestionIds.has(nextPremiseId))
      ) {
        return;
      }

      const options = isChoiceInputType(draftInputType)
        ? draftOptions[draftInputType].map((option) => option.trim()).filter(Boolean)
        : [];

      setSavingQuestionId(question.id);
      const didSave = await saveQuestion({
        ...question,
        premiseId: nextPremiseId,
        question: nextQuestionText,
        inputType: draftInputType,
        options,
        source: question.source,
        sentiment:
          question.source === "ai_generated" ? question.sentiment ?? null : null,
        dislikeReason:
          question.source === "ai_generated" &&
          question.sentiment === "dislike"
            ? question.dislikeReason ?? null
            : null,
      });

      setSavingQuestionId((current) =>
        current === question.id ? null : current,
      );
      if (didSave) {
        resetEditor();
      }
    },
    [
      allQuestions,
      coreResearchQuestionIds,
      draftPremiseId,
      draftInputType,
      draftOptions,
      draftQuestion,
      resetEditor,
      saveQuestion,
    ],
  );

  const handleDeleteQuestion = useCallback(
    async (questionId: string) => {
      setDeletingQuestionId(questionId);
      const didDelete = await deleteQuestion(questionId);
      setDeletingQuestionId((current) =>
        current === questionId ? null : current,
      );

      if (didDelete && editingId === questionId) {
        resetEditor();
      }
    },
    [deleteQuestion, editingId, resetEditor],
  );

  const resetDragState = useCallback(() => {
    setDraggingQuestionId(null);
    setDraggingSection(null);
    setDragOverQuestionId(null);
    setDragOverPosition(null);
  }, []);

  const getSectionQuestions = useCallback(
    (section: FeedbackFlowQuestionSection): FeedbackFlowQuestion[] => {
      switch (section) {
        case "demographics":
          return demographicsQuestions;
        case "personal_info":
          return personalInfoQuestions;
        case "prototype":
          return prototypeQuestions;
      }
    },
    [demographicsQuestions, personalInfoQuestions, prototypeQuestions],
  );

  const commitQuestionReorder = useCallback(
    async (
      section: FeedbackFlowQuestionSection,
      visibleQuestions: FeedbackFlowQuestion[],
      reorderedVisibleQuestionIds: string[],
    ) => {
      const fullSectionQuestionIds = getSectionQuestions(section).map(
        (question) => question.id,
      );
      const nextQuestionIds =
        reorderedVisibleQuestionIds.length === fullSectionQuestionIds.length
          ? reorderedVisibleQuestionIds
          : mergeVisibleQuestionIdsIntoSectionOrder(
              fullSectionQuestionIds,
              reorderedVisibleQuestionIds,
            );

      if (
        fullSectionQuestionIds.length === nextQuestionIds.length &&
        fullSectionQuestionIds.every(
          (questionId, index) => questionId === nextQuestionIds[index],
        )
      ) {
        return;
      }

      setReorderingSection(section);
      const didReorder = await reorderQuestions(section, nextQuestionIds);
      setReorderingSection((current) =>
        current === section ? null : current,
      );

      if (!didReorder && visibleQuestions.length > 0) {
        setDragOverQuestionId(null);
        setDragOverPosition(null);
      }
    },
    [getSectionQuestions, reorderQuestions],
  );

  const showReorderSectionWarning = useCallback(
    (section: FeedbackFlowQuestionSection) => {
      setReorderWarning(
        `Questions can only be rearranged within the ${SECTION_LABEL[section]} section.`,
      );
    },
    [],
  );

  const handleQuestionDragStart = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      questionId: string,
      section: FeedbackFlowQuestionSection,
    ) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", questionId);
      setReorderWarning(null);
      setDraggingQuestionId(questionId);
      setDraggingSection(section);
      setDragOverQuestionId(null);
      setDragOverPosition(null);
    },
    [],
  );

  const handleQuestionDragOver = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      questionId: string,
      section: FeedbackFlowQuestionSection,
    ) => {
      if (!draggingQuestionId || !draggingSection) {
        return;
      }

      event.preventDefault();

      if (draggingSection !== section) {
        setDragOverQuestionId(null);
        setDragOverPosition(null);
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const position: DragPosition =
        event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
      setDragOverQuestionId(questionId);
      setDragOverPosition(position);
    },
    [draggingQuestionId, draggingSection],
  );

  const handleSectionDragOver = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      section: FeedbackFlowQuestionSection,
    ) => {
      if (!draggingQuestionId || !draggingSection) {
        return;
      }

      event.preventDefault();

      if (draggingSection === section) {
        setDragOverQuestionId(null);
        setDragOverPosition(null);
      }
    },
    [draggingQuestionId, draggingSection],
  );

  const handleQuestionDrop = useCallback(
    async (
      event: DragEvent<HTMLDivElement>,
      targetQuestionId: string,
      section: FeedbackFlowQuestionSection,
      visibleQuestions: FeedbackFlowQuestion[],
    ) => {
      if (!draggingQuestionId || !draggingSection) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (draggingSection !== section) {
        showReorderSectionWarning(draggingSection);
        resetDragState();
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const position: DragPosition =
        dragOverQuestionId === targetQuestionId && dragOverPosition
          ? dragOverPosition
          : event.clientY < bounds.top + bounds.height / 2
            ? "before"
            : "after";
      const reorderedVisibleQuestionIds = reorderQuestionIds(
        visibleQuestions.map((question) => question.id),
        draggingQuestionId,
        targetQuestionId,
        position,
      );

      await commitQuestionReorder(
        section,
        visibleQuestions,
        reorderedVisibleQuestionIds,
      );
      resetDragState();
    },
    [
      commitQuestionReorder,
      dragOverPosition,
      dragOverQuestionId,
      draggingQuestionId,
      draggingSection,
      resetDragState,
      showReorderSectionWarning,
    ],
  );

  const handleSectionDrop = useCallback(
    async (
      event: DragEvent<HTMLDivElement>,
      section: FeedbackFlowQuestionSection,
      visibleQuestions: FeedbackFlowQuestion[],
    ) => {
      if (!draggingQuestionId || !draggingSection) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (draggingSection !== section) {
        showReorderSectionWarning(draggingSection);
        resetDragState();
        return;
      }

      const reorderedVisibleQuestionIds = visibleQuestions
        .map((question) => question.id)
        .filter((questionId) => questionId !== draggingQuestionId);

      reorderedVisibleQuestionIds.push(draggingQuestionId);

      await commitQuestionReorder(
        section,
        visibleQuestions,
        reorderedVisibleQuestionIds,
      );
      resetDragState();
    },
    [
      commitQuestionReorder,
      draggingQuestionId,
      draggingSection,
      resetDragState,
      showReorderSectionWarning,
    ],
  );

  const scrollToQuestionTarget = useCallback(
    (question: FeedbackFlowQuestion) => {
      if (!previewFrameRef?.current || !question.taggedTarget?.selector) {
        return;
      }

      const route = question.taggedTarget.route;
      if (route && route !== iframeRoute) {
        navigatePreviewToRoute(
          previewFrameRef.current,
          buildExperimentPreviewUrl(route, previewBaseUrl),
          question.taggedTarget.selector,
        );
        setActivePreview("product");
        return;
      }

      scrollPreviewToElement(
        previewFrameRef.current,
        question.taggedTarget.selector,
      );
      setActivePreview("product");
    },
    [iframeRoute, previewBaseUrl, previewFrameRef, setActivePreview],
  );

  const renderQuestionEditor = (question: FeedbackFlowQuestion) => {
    const isQuestionSaving = savingQuestionId === question.id;
    const isQuestionDeleting = deletingQuestionId === question.id;
    const isProductQuestion = question.section === "prototype";
    const hasValidPremiseSelection =
      !isProductQuestion ||
      Boolean(draftPremiseId && coreResearchQuestionIds.has(draftPremiseId));
    const isSaveDisabled =
      isReadOnly ||
      !draftQuestion.trim() ||
      !hasValidPremiseSelection ||
      isQuestionSaving ||
      isQuestionDeleting;

    return (
      <div className="relative p-2.5 space-y-2.5">
        {(isQuestionSaving || isQuestionDeleting) && (
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full border border-white/10 bg-black/80 px-2 py-1 text-[10px] text-amber-300">
            <Loader2 className="h-3 w-3 animate-spin" />
            {isQuestionDeleting ? "Deleting..." : "Saving..."}
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-400">
            Edit Question
          </span>
          <button
            type="button"
            onClick={resetEditor}
            disabled={isQuestionSaving || isQuestionDeleting}
            className="p-0.5 text-slate-500 hover:text-slate-300"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <textarea
          ref={questionInputRef}
          value={draftQuestion}
          onChange={(event) => setDraftQuestion(event.target.value)}
          disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
          placeholder="What feedback would you like to collect?"
          rows={2}
          className="w-full resize-none rounded-lg border border-white/20 bg-black/80 px-2.5 py-2 text-xs text-white placeholder-slate-500 transition-colors focus:border-amber-400 focus:outline-none"
        />

        {isProductQuestion && (
          <div className="space-y-1.5 rounded-lg border border-white/10 bg-black/40 p-2.5">
            <label
              htmlFor={`question-premise-${question.id}`}
              className="text-[10px] font-medium uppercase tracking-wide text-slate-400"
            >
              Core research question
            </label>
            {coreResearchQuestions.length > 0 ? (
              <select
                id={`question-premise-${question.id}`}
                value={draftPremiseId}
                onChange={(event) => setDraftPremiseId(event.target.value)}
                disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
                className="w-full rounded-lg border border-white/20 bg-black/80 px-2.5 py-2 text-xs text-white transition-colors focus:border-amber-400 focus:outline-none"
              >
                <option value="">Select a research question</option>
                {coreResearchQuestions.map((premise) => (
                  <option key={premise.id} value={premise.id}>
                    {premise.value}
                  </option>
                ))}
              </select>
            ) : (
              <p className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-2.5 py-2 text-[11px] leading-4 text-amber-100">
                Save or reload Research Foundation so this product question can be
                mapped before saving.
              </p>
            )}
            {coreResearchQuestions.length > 0 && !hasValidPremiseSelection ? (
              <p className="text-[10px] text-amber-300">
                Choose the core research question this product question answers.
              </p>
            ) : null}
          </div>
        )}

        <div className="grid grid-cols-2 gap-1">
          {QUESTION_TYPE_OPTIONS.map((typeOption) => {
            const Icon = typeOption.icon;
            const selected = draftInputType === typeOption.value;
            return (
              <button
                type="button"
                key={`${question.id}-${typeOption.value}`}
                onClick={() => setDraftInputType(typeOption.value)}
                disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[10px] font-medium transition-all ${
                  selected
                    ? "border border-amber-400/30 bg-amber-400/20 text-amber-400"
                    : "border border-white/10 bg-black/40 text-slate-400"
                }`}
              >
                <Icon className="w-3 h-3" />
                {typeOption.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {needsOptions(draftInputType) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-1.5 overflow-hidden"
            >
              {draftOptions[draftInputType as ChoiceInputType].map((option, index) => (
                <div
                  key={`${question.id}-option-${index}`}
                  className="flex items-center gap-1.5"
                >
                  <input
                    value={option}
                    onChange={(event) => {
                      if (!isChoiceInputType(draftInputType)) {
                        return;
                      }
                      setDraftOptions((previous) => {
                        const nextOptions = [...previous[draftInputType]];
                        nextOptions[index] = event.target.value;
                        return { ...previous, [draftInputType]: nextOptions };
                      });
                    }}
                    disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 rounded-lg border border-slate-600 bg-[#0F0F0F]/80 px-2.5 py-1.5 text-xs text-white placeholder-slate-500 transition-colors focus:border-amber-400 focus:outline-none"
                  />
                  {draftOptions[draftInputType as ChoiceInputType].length > 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!isChoiceInputType(draftInputType)) {
                          return;
                        }
                        setDraftOptions((previous) => ({
                          ...previous,
                          [draftInputType]: previous[draftInputType].filter(
                            (_, optionIndex) => optionIndex !== index,
                          ),
                        }));
                      }}
                      disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
                      className="p-1 text-red-400/60 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  if (!isChoiceInputType(draftInputType)) {
                    return;
                  }
                  setDraftOptions((previous) => ({
                    ...previous,
                    [draftInputType]: [...previous[draftInputType], ""],
                  }));
                }}
                disabled={isReadOnly || isQuestionSaving || isQuestionDeleting}
                className="flex w-full items-center justify-center gap-1 py-1 text-[10px] text-slate-500 transition-colors hover:text-slate-300"
              >
                <Plus className="w-3 h-3" />
                Add option
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => void handleSave(question.id)}
          disabled={isSaveDisabled}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-400 py-1.5 text-xs font-medium text-black transition-colors hover:bg-amber-500 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500"
        >
          <Save className="w-3.5 h-3.5" />
          {isQuestionSaving ? "Saving..." : "Save Question"}
        </button>
      </div>
    );
  };

  const renderQuestionList = (
    questions: FeedbackFlowQuestion[],
    section: FeedbackFlowQuestionSection,
    emptyLabel: string,
  ) => {
    if (questions.length === 0) {
      return (
        <div className="py-4 text-center text-xs text-slate-500">
          {emptyLabel}
        </div>
      );
    }

    return questions.map((question, index) => {
      const isEditing = editingId === question.id;
      const isSectionReordering = reorderingSection === section;
      const isQuestionSaving = savingQuestionId === question.id;
      const isQuestionDeleting = deletingQuestionId === question.id;
      const isQuestionPending =
        isQuestionSaving || isQuestionDeleting || isSectionReordering;
      const isTagged = isTaggedFeedbackQuestion(question);
      const route = getQuestionRoute(question);
      const isOtherPage = Boolean(route && route !== iframeRoute);
      const optionCount = question.options.filter((option) => option.trim()).length;
      const isDragTarget = dragOverQuestionId === question.id;
      const canDragQuestion =
        questions.length > 1 &&
        !isReadOnly &&
        !isEditing &&
        !isQuestionPending &&
        !isInitializing;
      const reactionDraft = getQuestionReactionDraft(
        question,
        reactionDrafts[question.id],
      );
      const mappedResearchQuestion = question.premiseId?.trim()
        ? coreResearchQuestionById.get(question.premiseId.trim())
        : undefined;
      const researchQuestionPillClassName = mappedResearchQuestion
        ? RESEARCH_QUESTION_PILL_STYLES[
            mappedResearchQuestion.index % RESEARCH_QUESTION_PILL_STYLES.length
          ]
        : "";
      const showAiReactionControls = question.source === "ai_generated";
      const isReactionSaving =
        isQuestionSaving && savingQuestionId === question.id;
      const isReactionDislikeSelected = reactionDraft.sentiment === "dislike";
      const isDislikeReasonValid = Boolean(reactionDraft.dislikeReason.trim());

      return (
        <div
          key={question.id}
          draggable={canDragQuestion}
          onDragStart={(event) =>
            handleQuestionDragStart(event, question.id, section)
          }
          onDragEnd={resetDragState}
          onDragOver={(event) =>
            handleQuestionDragOver(event, question.id, section)
          }
          onDrop={(event) =>
            void handleQuestionDrop(event, question.id, section, questions)
          }
          className={`rounded-lg border transition-all ${
            isEditing
              ? "border-amber-400/40 bg-[#0F0F0F]"
              : "border-white/10 bg-[#0F0F0F]/50 hover:border-white/20"
          } ${isQuestionPending ? "opacity-80" : ""} ${
            canDragQuestion ? "cursor-grab active:cursor-grabbing" : ""
          } ${
            isDragTarget
              ? dragOverPosition === "before"
                ? "border-t-amber-400"
                : "border-b-amber-400"
              : ""
          }`}
        >
          {isEditing ? (
            renderQuestionEditor(question)
          ) : (
            <div className="space-y-2 px-2.5 py-2">
              <div className="flex items-start gap-2">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-400/20 text-[10px] font-bold text-amber-400">
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium leading-snug text-slate-200">
                    {question.question || "Untitled question"}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span className="rounded bg-white/5 px-1.5 py-0.5 text-[9px] text-slate-400">
                      {QUESTION_TYPE_LABEL[question.inputType]}
                    </span>
                    {needsOptions(question.inputType) && optionCount > 0 && (
                      <span className="text-[9px] text-slate-500">
                        {optionCount} options
                      </span>
                    )}
                    {question.section === "prototype" &&
                      question.source === "ai_generated" && (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-400/10 px-1.5 py-0.5 text-[9px] text-amber-300">
                          <Sparkles className="h-2.5 w-2.5" />
                          AI
                        </span>
                      )}
                    {isQuestionPending && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-400/10 px-1.5 py-0.5 text-[9px] text-amber-300">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        {isQuestionDeleting
                          ? "Deleting"
                          : isSectionReordering
                            ? "Reordering"
                            : "Saving"}
                      </span>
                    )}
                    {isTagged && (
                      <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[9px] text-emerald-400">
                        Tagged
                      </span>
                    )}
                    {question.section === "prototype" && mappedResearchQuestion ? (
                      <span
                        className={`inline-flex items-center rounded-full border px-1.5 py-px text-[8px] font-semibold leading-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${researchQuestionPillClassName}`}
                        title={mappedResearchQuestion.value}
                      >
                        RQ{mappedResearchQuestion.index + 1}
                      </span>
                    ) : null}
                    {question.section === "prototype" &&
                      !question.premiseId?.trim() && (
                        <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] text-amber-300">
                          Needs research question
                        </span>
                      )}
                  </div>
                  {section === "prototype" && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {isTagged && question.taggedTarget?.selector ? (
                        <button
                          type="button"
                          onClick={() => scrollToQuestionTarget(question)}
                          className="flex items-center gap-1 text-[10px] text-amber-400 transition-colors hover:text-amber-300"
                        >
                          {isOtherPage ? (
                            <>
                              <Navigation className="w-3 h-3" />
                              Go to page
                            </>
                          ) : (
                            <>
                              <Eye className="w-3 h-3" />
                              Scroll to element
                            </>
                          )}
                        </button>
                      ) : section === "prototype" ? (
                        <button
                          type="button"
                          onClick={() => onTagExistingQuestion(question.id)}
                          disabled={isEditingDisabled || isQuestionPending}
                          className="flex items-center gap-1 text-[10px] text-blue-400 transition-colors hover:text-blue-300"
                        >
                          <PenTool className="w-3 h-3" />
                          Tag to element
                        </button>
                      ) : null}
                      {route && (
                        <span className="max-w-[140px] truncate text-[9px] text-slate-600">
                          {route}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-0.5">
                  <span
                    className={`p-1 ${
                      canDragQuestion ? "text-slate-500" : "text-slate-700"
                    }`}
                    title="Drag anywhere on this card to rearrange within the section"
                  >
                    <GripVertical className="w-3 h-3" />
                  </span>
                  <button
                    type="button"
                    onClick={() => startEditing(question)}
                    disabled={isReadOnly || isQuestionPending}
                    className="p-1 text-slate-500 transition-colors hover:text-amber-400"
                    title="Edit"
                    aria-label="Edit question"
                  >
                    <PencilLine className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteQuestion(question.id)}
                    disabled={isReadOnly || isQuestionPending}
                    className="p-1 text-slate-500 transition-colors hover:text-red-400"
                    title="Delete"
                  >
                    {isQuestionDeleting ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                  </button>
                </div>
              </div>

              {showAiReactionControls && (
                <div className="w-full space-y-2">
                  <div className="flex w-full flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setQuestionReactionDraft(question, {
                          sentiment: "like",
                          dislikeReason: "",
                        });
                        void saveQuestionReaction(question, "like");
                      }}
                      disabled={isReadOnly || isQuestionPending}
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                        reactionDraft.sentiment === "like"
                          ? "border-amber-400/40 bg-amber-400/15 text-amber-300"
                          : "border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200"
                      }`}
                      title="Like"
                      aria-label="Like question"
                    >
                      <ThumbsUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setQuestionReactionDraft(question, {
                          sentiment: "dislike",
                        })
                      }
                      disabled={isReadOnly || isQuestionPending}
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
                        reactionDraft.sentiment === "dislike"
                          ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                          : "border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200"
                      }`}
                      title="Dislike"
                      aria-label="Dislike question"
                    >
                      <ThumbsDown className="h-3 w-3" />
                    </button>
                    {isReactionSaving && (
                      <span className="inline-flex items-center gap-1 text-[9px] text-amber-300">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Saving
                      </span>
                    )}
                  </div>

                  {isReactionDislikeSelected && (
                    <div className="w-full space-y-1.5 rounded-lg border border-rose-500/20 bg-rose-500/5 p-2">
                      <div className="flex items-center gap-1 text-[10px] font-medium text-rose-200">
                        <ThumbsDown className="h-3 w-3" />
                        Why is this not useful?
                      </div>
                      <textarea
                        id={`question-card-dislike-${question.id}`}
                        value={reactionDraft.dislikeReason}
                        onChange={(event) =>
                          setQuestionReactionDraft(question, {
                            sentiment: "dislike",
                            dislikeReason: event.target.value,
                          })
                        }
                        disabled={isReadOnly || isQuestionPending}
                        readOnly={isReadOnly}
                        placeholder="Add the reason"
                        rows={2}
                        className="w-full resize-none rounded-lg border border-rose-500/30 bg-black/70 px-2.5 py-2 text-xs text-white placeholder-slate-500 transition-colors focus:border-rose-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          void saveQuestionReaction(
                            question,
                            "dislike",
                            reactionDraft.dislikeReason,
                          )
                        }
                        disabled={
                          isReadOnly ||
                          isQuestionPending ||
                          !isDislikeReasonValid
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-200 transition-colors hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/5 disabled:text-slate-500"
                      >
                        <Save className="h-3 w-3" />
                        Save
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const renderIntroTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-2.5">
        <button
          type="button"
          onClick={() => feedbackFlow.intro.enabled && setActivePreview("intro")}
          className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
            activePreview === "intro"
              ? "text-amber-400"
              : feedbackFlow.intro.enabled
                ? "text-white hover:text-amber-400"
                : "text-slate-500"
          }`}
        >
          <Eye className="h-3.5 w-3.5" />
          Preview intro
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            {feedbackFlow.intro.enabled ? "On" : "Off"}
          </span>
          <button
            type="button"
            onClick={() =>
              updateIntro((current) => ({ ...current, enabled: !current.enabled }))
            }
            disabled={isEditingDisabled}
            className={`mt-0.5 flex h-4 w-8 items-center rounded-full p-0.5 transition-colors ${
              feedbackFlow.intro.enabled
                ? "justify-end bg-amber-400"
                : "justify-start bg-slate-700"
            }`}
          >
            <div className="h-3 w-3 rounded-full bg-black shadow-sm" />
          </button>
        </div>
      </div>

      {feedbackFlow.intro.enabled && (
        <div className="space-y-2">
          <textarea
            value={feedbackFlow.intro.description}
            onChange={(event) =>
              updateIntro((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            disabled={isReadOnly}
            rows={5}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
            placeholder="Welcome description"
          />
          <textarea
            value={feedbackFlow.intro.terms ?? ""}
            onChange={(event) =>
              updateIntro((current) => ({
                ...current,
                terms: event.target.value,
              }))
            }
            disabled={isReadOnly}
            rows={3}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
            placeholder="Optional terms and privacy details"
          />
        </div>
      )}
    </div>
  );

  const renderDemographicsTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white">Enabled</span>
          <button
            type="button"
            onClick={() =>
              setSectionEnabled("demographics", !feedbackFlow.demographicsEnabled)
            }
            disabled={isEditingDisabled || isSyncing}
            className={`mt-0.5 flex h-4 w-8 items-center rounded-full p-0.5 transition-colors ${
              feedbackFlow.demographicsEnabled
                ? "justify-end bg-amber-400"
                : "justify-start bg-slate-700"
            }`}
          >
            <div className="h-3 w-3 rounded-full bg-black shadow-sm" />
          </button>
        </div>

        {feedbackFlow.demographicsEnabled && (
          <button
            type="button"
            onClick={() => addQuestion("demographics")}
            disabled={isEditingDisabled}
            className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-medium text-amber-300 transition-colors hover:bg-amber-500/15"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        )}
      </div>

      <div
        data-feedback-flow-section="demographics"
        onDragOver={(event) => handleSectionDragOver(event, "demographics")}
        onDrop={(event) =>
          void handleSectionDrop(event, "demographics", demographicsQuestions)
        }
        className="space-y-1.5 pr-1"
      >
        {renderQuestionList(
          demographicsQuestions,
          "demographics",
          feedbackFlow.demographicsEnabled
            ? "No demographics questions yet."
            : "Demographics questions are disabled.",
        )}
      </div>
    </div>
  );

  const renderPersonalInfoTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-white">Enabled</span>
          <button
            type="button"
            onClick={() =>
              setSectionEnabled("personal_info", !feedbackFlow.personalInfoEnabled)
            }
            disabled={isEditingDisabled || isSyncing}
            className={`mt-0.5 flex h-4 w-8 items-center rounded-full p-0.5 transition-colors ${
              feedbackFlow.personalInfoEnabled
                ? "justify-end bg-amber-400"
                : "justify-start bg-slate-700"
            }`}
          >
            <div className="h-3 w-3 rounded-full bg-black shadow-sm" />
          </button>
        </div>

        {feedbackFlow.personalInfoEnabled && (
          <button
            type="button"
            onClick={() => addQuestion("personal_info")}
            disabled={isEditingDisabled}
            className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-medium text-amber-300 transition-colors hover:bg-amber-500/15"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        )}
      </div>

      <div
        data-feedback-flow-section="personal_info"
        onDragOver={(event) => handleSectionDragOver(event, "personal_info")}
        onDrop={(event) =>
          void handleSectionDrop(event, "personal_info", personalInfoQuestions)
        }
        className="space-y-1.5 pr-1"
      >
        {renderQuestionList(
          personalInfoQuestions,
          "personal_info",
          feedbackFlow.personalInfoEnabled
            ? "No personal info questions yet."
            : "Personal info questions are disabled.",
        )}
      </div>
    </div>
  );

  const renderOutroTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-2.5">
        <button
          type="button"
          onClick={() => feedbackFlow.outro.enabled && setActivePreview("outro")}
          className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
            activePreview === "outro"
              ? "text-amber-400"
              : feedbackFlow.outro.enabled
                ? "text-white hover:text-amber-400"
                : "text-slate-500"
          }`}
        >
          <Eye className="h-3.5 w-3.5" />
          Preview outro
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            {feedbackFlow.outro.enabled ? "On" : "Off"}
          </span>
          <button
            type="button"
            onClick={() =>
              updateOutro((current) => ({ ...current, enabled: !current.enabled }))
            }
            disabled={isEditingDisabled}
            className={`mt-0.5 flex h-4 w-8 items-center rounded-full p-0.5 transition-colors ${
              feedbackFlow.outro.enabled
                ? "justify-end bg-amber-400"
                : "justify-start bg-slate-700"
            }`}
          >
            <div className="h-3 w-3 rounded-full bg-black shadow-sm" />
          </button>
        </div>
      </div>

      {feedbackFlow.outro.enabled && (
        <div className="space-y-2">
          <textarea
            value={feedbackFlow.outro.description}
            onChange={(event) =>
              updateOutro((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            disabled={isReadOnly}
            rows={4}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
            placeholder="Thank-you description"
          />
          <textarea
            value={feedbackFlow.outro.terms ?? ""}
            onChange={(event) =>
              updateOutro((current) => ({
                ...current,
                terms: event.target.value,
              }))
            }
            disabled={isReadOnly}
            rows={3}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
            placeholder="Optional outro terms or next steps"
          />
        </div>
      )}
    </div>
  );

  const renderWorkspaceTabs = () => (
    <div className="flex-shrink-0 px-3 pt-3">
      <div className="rounded-[22px] border border-white/10 bg-[#0B0B0B] p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.35)]">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={openSetupOverview}
            className={`rounded-[18px] border px-3 py-2 text-left transition-all ${
              sidebarWorkspace === "setup"
                ? "border-amber-400/35 bg-amber-400/12 text-white"
                : "border-white/10 bg-black/30 text-slate-400 hover:border-white/20 hover:text-white"
            }`}
          >
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-amber-400" />
              <span className="text-[11px] font-semibold">Setup</span>
            </div>
          </button>

          <button
            type="button"
            onClick={openQuestionsWorkspace}
            className={`rounded-[18px] border px-3 py-2 text-left transition-all ${
              sidebarWorkspace === "questions"
                ? "border-amber-400/35 bg-amber-400/12 text-white"
                : "border-white/10 bg-black/30 text-slate-400 hover:border-white/20 hover:text-white"
            }`}
          >
            <div className="flex items-center gap-2">
              <MessageSquarePlus className="h-4 w-4 text-amber-400" />
              <span className="text-[11px] font-semibold">Interview Questions</span>
            </div>
          </button>
        </div>

        {sidebarWorkspace === "setup" && (
          <div className="mt-2 grid grid-cols-4 gap-1.5 border-t border-white/10 pt-2">
            {setupTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = setupTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleSetupShortcut(tab.id)}
                  className={`rounded-2xl border px-2 py-2 text-center transition-all ${
                    isActive
                      ? "border-amber-400/35 bg-amber-400/12 text-white"
                      : "border-white/10 bg-black/20 text-slate-400 hover:border-white/20 hover:text-white"
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full ${
                        tab.enabled
                          ? "bg-amber-400/12 text-amber-300"
                          : "bg-white/5 text-slate-500"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="truncate text-[10px] font-medium">
                      {tab.shortLabel}
                    </span>
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        tab.enabled ? "bg-emerald-400" : "bg-white/15"
                      }`}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderSetupWorkspace = () => (
    <div className="flex min-h-0 flex-1 flex-col px-3 pb-3 pt-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[#080808] shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
        <div className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(217,182,75,0.06),rgba(10,10,10,0.98))] px-4 py-3">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-2xl border ${
                activeSetupTab.enabled
                  ? "border-amber-400/25 bg-amber-400/10 text-amber-300"
                  : "border-white/10 bg-white/5 text-slate-500"
              }`}
            >
              <activeSetupTab.icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">
                {activeSetupTab.label}
              </p>
            </div>
            <button
              type="button"
              onClick={openQuestionsWorkspace}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-white transition-colors hover:border-white/20 hover:bg-white/10"
            >
              <MessageSquarePlus className="h-3 w-3" />
              Back
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {setupTab === "intro" && renderIntroTab()}
          {setupTab === "demographics" && renderDemographicsTab()}
          {setupTab === "personalInfo" && renderPersonalInfoTab()}
          {setupTab === "outro" && renderOutroTab()}
        </div>
      </div>
    </div>
  );

  const renderQuestionsWorkspace = () => (
    <div className="flex min-h-0 flex-1 flex-col px-3 pb-3 pt-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[#080808] shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
        <div className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(7,7,7,0.98))] px-4 py-3">
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] leading-4 text-slate-300">
            <Activity className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-300" />
            <span>
              Behavioral telemetry (scroll depth, time on screen, click heatmaps)
              will be automatically tracked and collected.
            </span>
          </div>

          <div className="flex items-center gap-2">
            <MessageSquarePlus className="h-4 w-4 text-amber-400" />
            <button
              type="button"
              onClick={() => setActivePreview("product")}
              className={`text-sm font-semibold transition-colors ${
                activePreview === "product"
                  ? "text-amber-400"
                  : "text-white hover:text-amber-400"
              }`}
            >
              Interview Questions
            </button>
          </div>

          <div className="mt-3 flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSelectedResearchQuestionId(null)}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                  !selectedResearchQuestionId
                    ? "bg-white/20 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                All: {prototypeQuestions.length}
              </button>
              {coreResearchQuestions.map((question, index) => {
                const isActive = selectedResearchQuestionId === question.id;
                const styleClass =
                  RESEARCH_QUESTION_PILL_STYLES[
                    index % RESEARCH_QUESTION_PILL_STYLES.length
                  ];

                return (
                  <button
                    key={question.id}
                    type="button"
                    title={question.value}
                    onClick={() => setSelectedResearchQuestionId(question.id)}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-medium transition-colors ${
                      isActive
                        ? styleClass
                        : "border-white/10 bg-black/30 text-slate-400 hover:border-white/20 hover:text-slate-200"
                    }`}
                  >
                    RQ{index + 1}: {researchQuestionCounts.get(question.id) ?? 0}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                addQuestion("prototype");
                setActivePreview("product");
              }}
              disabled={activePreview !== "product" || isEditingDisabled}
              className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                activePreview !== "product" || isEditingDisabled
                  ? "cursor-not-allowed border border-white/10 bg-white/5 text-slate-600"
                  : "border border-amber-400/30 bg-amber-400/15 text-amber-200 hover:bg-amber-400/20"
              }`}
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          </div>

          {activePreview !== "product" && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100">
              <span className="min-w-0">
                Switch back to Product preview to add questions or tag elements.
              </span>
              <button
                type="button"
                onClick={openQuestionsWorkspace}
                className="flex-shrink-0 text-amber-300 transition-colors hover:text-white"
              >
                Go to product
              </button>
            </div>
          )}
        </div>

        <div
          data-feedback-flow-section="prototype"
          onDragOver={(event) => handleSectionDragOver(event, "prototype")}
          onDrop={(event) =>
            void handleSectionDrop(event, "prototype", filteredPrototypeQuestions)
          }
          className="relative min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 py-3"
        >
          {isGenerating && (
            <div className="mx-3 mb-3 rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Generating questions...</span>
              </div>
            </div>
          )}

          {isGenerating ? null : !isPreviewReady ? (
            <div className="flex flex-col items-center justify-center py-12 text-sm text-gray-500">
              <span>Waiting for preview...</span>
            </div>
          ) : (
            <>
              {filteredPrototypeQuestions.length === 0 && (
                <div className="rounded-[20px] border border-dashed border-white/10 bg-black/20 px-4 py-8 text-center">
                  <p className="text-sm text-slate-400">No interview questions in this view</p>
                  <p className="mt-1 text-xs text-slate-600">
                    {selectedResearchQuestionId
                      ? "Select another research question or view all interview questions."
                      : "Add a question or use Draw mode to tag an element."}
                  </p>
                </div>
              )}

              {renderQuestionList(
                filteredPrototypeQuestions,
                "prototype",
                "No interview questions yet.",
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (isInitializing) {
    return (
      <div className="flex h-full flex-col bg-black">
        <div className="border-b border-white/10 px-3 py-3">
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-[#0F0F0F] p-1">
            <LoadingBlock className="h-8" />
            <LoadingBlock className="h-8" />
          </div>
        </div>

        <div className="flex-shrink-0 px-3 pt-3">
          <div className="rounded-[22px] border border-white/10 bg-[#0F0F0F] p-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              <LoadingBlock className="h-12" />
              <LoadingBlock className="h-12" />
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-3 pb-3 pt-3">
          <div className="flex min-h-0 flex-1 flex-col rounded-[22px] border border-white/10 bg-[#0F0F0F] p-3">
            <div className="space-y-3 border-b border-white/10 pb-3">
              <div className="flex items-center gap-3">
                <LoadingBlock className="h-4 w-36" />
                <LoadingBlock className="ml-auto h-6 w-24 rounded-full" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <LoadingBlock className="h-7 w-20 rounded-full" />
                  <LoadingBlock className="h-7 w-20 rounded-full" />
                </div>
                <LoadingBlock className="h-8 w-28 rounded-full" />
              </div>
            </div>

            <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={`question-skeleton-${index}`}
                  className="rounded-lg border border-white/10 bg-[#0F0F0F]/50 px-2.5 py-2"
                >
                  <div className="flex items-start gap-2">
                    <LoadingBlock className="mt-0.5 h-6 w-6 rounded-full" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <LoadingBlock className="h-3.5 w-3/4" />
                      <div className="flex flex-wrap gap-1.5">
                        <LoadingBlock className="h-4 w-20" />
                        <LoadingBlock className="h-4 w-16" />
                      </div>
                      <LoadingBlock className="h-3 w-24" />
                    </div>
                    <div className="flex gap-1">
                      <LoadingBlock className="h-5 w-5" />
                      <LoadingBlock className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col bg-black"
      onDragOver={(event) => {
        if (!isReadOnly && draggingQuestionId) {
          event.preventDefault();
        }
      }}
      onDrop={(event) => {
        if (isReadOnly || !draggingQuestionId || !draggingSection) {
          return;
        }

        const targetElement = event.target as HTMLElement | null;
        const dropSection = targetElement?.closest("[data-feedback-flow-section]");

        if (dropSection) {
          return;
        }

        event.preventDefault();
        showReorderSectionWarning(draggingSection);
        resetDragState();
      }}
    >
      <div className="flex-shrink-0 border-b border-white/10 px-3 py-3">
        <div
          className={`relative flex rounded-lg p-0.5 transition-all ${
            activePreview !== "product"
              ? "bg-[#0F0F0F]/50 opacity-40 pointer-events-none"
              : "bg-[#0F0F0F]"
          }`}
        >
          {activePreview !== "product" && (
            <div className="absolute inset-0 z-10 cursor-not-allowed rounded-lg" />
          )}
          <button
            type="button"
            onClick={() => onTaggingModeChange("draw")}
            disabled={activePreview !== "product" || isEditingDisabled}
            className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-all ${
              taggingMode === "draw"
                ? "border border-amber-400/30 bg-amber-400/20 text-amber-400"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <PenTool className="w-3.5 h-3.5" />
              Draw
            </span>
          </button>
          <button
            type="button"
            onClick={() => onTaggingModeChange("view")}
            disabled={activePreview !== "product" || isInitializing}
            className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-all ${
              taggingMode === "view"
                ? "border border-amber-400/30 bg-amber-400/20 text-amber-400"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              View
            </span>
          </button>
        </div>
        {activePreview !== "product" && (
          <p className="mt-1 text-center text-[10px] text-slate-500">
            Switch to{" "}
            <button
              type="button"
              onClick={() => setActivePreview("product")}
              className="text-amber-400 hover:underline"
            >
              Product
            </button>{" "}
            to tag elements.
          </p>
        )}
      </div>

      {reorderWarning ? (
        <div className="px-3 pt-2">
          <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100">
            <span className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-300" />
              <span>{reorderWarning}</span>
            </span>
            <button
              type="button"
              onClick={() => setReorderWarning(null)}
              className="text-amber-200 transition-colors hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}

      {renderWorkspaceTabs()}

      {sidebarWorkspace === "setup"
        ? renderSetupWorkspace()
        : renderQuestionsWorkspace()}
    </div>
  );
}
